/**
 * RP4 — Overlay-state derivation helpers extracted from `Scene3DStage.tsx`.
 *
 * Three small effects that mutate `overlayState`:
 *
 *   - **BEDIT toggle** — sets `beditActive` + a fallback `beditBounds`.
 *   - **Print-preview** — projects the world-space bounds rect to client
 *     pixels via `projectWorldToClient` whenever the prop or camera
 *     snapshot version changes.
 *   - **Cursor sysvars** — wires CURSORSIZE / CURSORCOLOR / APERTURE /
 *     AUTOSNAP-aperture-bit into the overlay state slice.
 *
 * The helpers return reducer-shaped functions the React effects call
 * inside their `setOverlayState((prev) => …)` lambdas, keeping the
 * orchestrator free of inline projection math.
 */

import * as React from "react";

import type { OverlayState } from "../overlays";
import { projectWorldToClient } from "../cameraSnapshot";
import { cursorColorCssFromAci } from "../overlays";
import { GRIP_ARMED_CURSOR } from "../grips/gripInteractionConstants";

export interface PrintPreviewWorldBounds {
  minX: number;
  minY: number;
  maxX: number;
  maxY: number;
  label?: string;
}

/**
 * BEDIT toggle reducer. Fallback bounds (50×50 around origin) used
 * when block bbox is unknown (matches SVG renderer fallback).
 */
export function buildBeditOverlayPatch(beditActive: boolean) {
  return (prev: OverlayState): OverlayState => ({
    ...prev,
    beditActive,
    beditBounds: beditActive
      ? prev.beditBounds ?? { left: -50, top: -50, right: 50, bottom: 50 }
      : null,
  });
}

/**
 * Print-preview reducer. Returns an updater that projects the world-
 * bounds rect into client pixels and writes the resulting visible
 * `printPreviewBounds`. When `bounds` is null the print preview is
 * cleared.
 *
 * Returns `null` if projection failed (camera snapshot not yet
 * populated) — caller should bail rather than overwrite.
 */
export function buildPrintPreviewOverlayPatch(
  bounds: PrintPreviewWorldBounds | null,
): ((prev: OverlayState) => OverlayState) | null {
  if (!bounds) {
    return (prev) => {
      if (!prev.printPreviewBounds) return prev;
      return { ...prev, printPreviewBounds: null };
    };
  }
  const { minX, minY, maxX, maxY } = bounds;
  const pTop = projectWorldToClient(minX, maxY);
  const pBottom = projectWorldToClient(maxX, minY);
  if (!pTop || !pBottom) return null;
  const left = Math.min(pTop.x, pBottom.x);
  const top = Math.min(pTop.y, pBottom.y);
  const width = Math.abs(pBottom.x - pTop.x);
  const height = Math.abs(pBottom.y - pTop.y);
  const labelProp =
    bounds.label !== undefined ? { paperSize: bounds.label } : {};
  return (prev) => ({
    ...prev,
    printPreviewBounds: {
      visible: true,
      rectScreen: { x: left, y: top, width, height },
      ...labelProp,
    },
  });
}

/**
 * Cursor / aperture sysvar reducer. Maps CURSORSIZE → cursorSizePct,
 * CURSORCOLOR → cursorColorCss, AUTOSNAP bit 8 → showAperture (APBOX),
 * APERTURE → pickboxPx, PICKBOX → selectionPickboxHalfPx.
 */
export function buildCursorSysvarOverlayPatch(args: {
  cursorSize: number;
  cursorColor: number;
  autosnap: number;
  aperture: number;
  pickbox: number;
}) {
  const colorCss = cursorColorCssFromAci(args.cursorColor);
  return (prev: OverlayState): OverlayState => ({
    ...prev,
    cursorSizePct: args.cursorSize,
    cursorColorCss: colorCss,
    // AUTOSNAP bit 8 = APBOX (aperture display).
    showAperture: (args.autosnap & 8) !== 0,
    pickboxPx: args.aperture,
    selectionPickboxHalfPx: args.pickbox,
  });
}

/**
 * GRIPS-INTERACTION (Codex P3 re-audit) — the stage-shell CSS cursor value,
 * by priority: ARMED grip (modal crosshair) > hover-over-entity (pointer) >
 * crosshair overlay visible (none) > default (""). Pure so the priority
 * logic itself is unit-testable without mounting the R3F Canvas — the
 * `Scene3DStage` cursor-policy effect just calls this and writes the result.
 *
 * The armed branch is TOP priority: previously the effect had no notion of
 * "armed" in its own priority chain, so a hover change (moving the pointer
 * over / away from an entity) DURING the armed phase would re-run the effect
 * and stomp `StageGripDrag`'s direct `GRIP_ARMED_CURSOR` write with `pointer`
 * / `none` before the user's second (commit) click — the modal crosshair
 * was not reliable for the armed phase's whole duration. Giving `armed` top
 * priority means every re-run while armed re-asserts the crosshair
 * regardless of hover churn.
 */
export function resolveStageCursor(args: {
  armed: boolean;
  hoveringEntity: boolean;
  crosshairVisible: boolean;
}): string {
  if (args.armed) return GRIP_ARMED_CURSOR;
  if (args.hoveringEntity) return "pointer";
  if (args.crosshairVisible) return "none";
  return "";
}

/**
 * Convenience wrapper combining `setOverlayState` calls into a single
 * effect-friendly closure for the print-preview path. Intended to be
 * called inside `useEffect`.
 */
// Phase 1.g (П35Р React #185 fix, codex P1 follow-up) — module-level
// tracker for whether the print-preview overlay currently shows on
// screen. Lets `applyPrintPreview` distinguish between:
//   1. A persistent null-bounds steady state (dialog closed, state
//      already cleared, no work to do, no setter call) — the case
//      that trips React error #185 when the caller's useFrame-driven
//      effect re-fires every frame.
//   2. The non-null → null transition (dialog just closed) where the
//      setter MUST fire once to clear `overlayState.printPreviewBounds`.
//
// The flag flips:
//   - to `true` whenever `applyPrintPreview` invokes the setter with a
//     non-null bounds patch (the patch's reducer adds the visible rect).
//   - to `false` after the clear-setter call returns.
//
// Module-level scope is acceptable because the print-preview overlay is
// a singleton (one Print/Plot dialog active at a time), and the same
// `Scene3DStage` mount drives the only caller.
let isPrintPreviewVisible = false;

export function applyPrintPreview(
  setOverlayState: React.Dispatch<React.SetStateAction<OverlayState>>,
  bounds: PrintPreviewWorldBounds | null,
): void {
  if (!bounds) {
    // Steady-state guard: already cleared → skip setter call to keep
    // the per-frame useEffect cheap. This is what avoids React #185.
    if (!isPrintPreviewVisible) return;
    isPrintPreviewVisible = false;
    // Clear path — codex P1: the non-null → null transition MUST still
    // fire the setter once, otherwise the rect remains on screen after
    // the Print/Plot dialog closes.
    setOverlayState((prev) => {
      if (!prev.printPreviewBounds) return prev;
      return { ...prev, printPreviewBounds: null };
    });
    return;
  }
  isPrintPreviewVisible = true;
  const patch = buildPrintPreviewOverlayPatch(bounds);
  if (patch) setOverlayState(patch);
}

/**
 * Test-only reset for the module-level `isPrintPreviewVisible` flag.
 * Vitest resets module state between test files, but within a single
 * file we may want to start each `applyPrintPreview` test from the
 * cleared baseline. Not exported to production callers.
 */
export function __resetApplyPrintPreviewForTests(): void {
  isPrintPreviewVisible = false;
}
